import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { FlagsProvider, useFlags } from '@atlaskit/flag';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { errorToastProps, successToastProps } from '@atlassian/eoc-common';
import { initializeGlobalState, sendScreenEvent, sendTrackEvent, sendUIEvent } from '../../common/utils';
import { isSSR } from '../../common/utils/is-ssr';
import { BSCIntlProvider } from '../../common/utils/locale';
import { Logger } from '../../common/utils/logger';
import { ACCEPT_ALL_PREFERENCES, ATLASSIAN_SHOW_BANNER_COOKIE, ONLY_NECESSARY_PREFERENCES } from '../../constants';
import { getCookie } from '../../controllers/cookie-controls/get-cookie';
import { handleInitialPreferencesData } from '../../controllers/cookie-controls/transformer';
import { getPreferences } from '../../controllers/storage-preferences/get-preferences';
import { updatePreferences } from '../../controllers/storage-preferences/update-preferences';
import { PrefetchedPreferenceState } from '../../types';
import { i18n } from './messages';
import { PreferenceModal } from './preference-modal';
import { PresentationalConsentBanner } from './presentational-consent-banner';
import usePrevious from './use-previous';
export var ConsentBanner = function ConsentBanner(props) {
  // Feature flag check. ESLint rules don't allow combined flags...
  if (getBooleanFF('platform.moonjelly.browser-storage-controls')) {
    return /*#__PURE__*/React.createElement(FlagsProvider, null, /*#__PURE__*/React.createElement(ConsentBannerImpl, props));
  }
  if (getBooleanFF('platform.moonjelly.browser-storage-controls-v2')) {
    return /*#__PURE__*/React.createElement(FlagsProvider, null, /*#__PURE__*/React.createElement(ConsentBannerImpl, props));
  }
  return null;
};
var ConsentBannerImpl = function ConsentBannerImpl(_ref) {
  var onShow = _ref.onShow,
    onHide = _ref.onHide,
    _ref$initialPreferenc = _ref.initialPreferences,
    initialPreferences = _ref$initialPreferenc === void 0 ? PrefetchedPreferenceState.INTERNAL : _ref$initialPreferenc,
    _ref$minWidth = _ref.minWidth,
    minWidth = _ref$minWidth === void 0 ? 400 : _ref$minWidth,
    product = _ref.product,
    _ref$analyticsEnabled = _ref.analyticsEnabled,
    analyticsEnabled = _ref$analyticsEnabled === void 0 ? true : _ref$analyticsEnabled;
  useEffect(function () {
    initializeGlobalState({
      analyticsEnabled: analyticsEnabled,
      product: product
    });
  }, [analyticsEnabled, product]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isPreferencesModalOpen = _useState2[0],
    setIsPreferencesModalOpen = _useState2[1];
  var prefetchedPreferencesData = useRef(initialPreferences);
  var _useState3 = useState(handleInitialPreferencesData(initialPreferences)),
    _useState4 = _slicedToArray(_useState3, 2),
    preferences = _useState4[0],
    setPreferences = _useState4[1];
  useEffect(function () {
    // On initial render, this will either set the proper preferences in state + required cookies based on the
    // prefetched data, or fetch all of that data from ConsentHub and then set appropriate cookies + state.
    var loadStoredPrefs = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var prefs;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return getPreferences();
            case 2:
              prefs = _context.sent;
              // If something has gone wrong during this `get` process (invalid token, failed request, etc.)
              // these preferences will be undefined and we'll need to show the banner as we're in an unknown state
              setPreferences(prefs);
            case 4:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return function loadStoredPrefs() {
        return _ref2.apply(this, arguments);
      };
    }();
    var unpackedPrefetchedPrefs = handleInitialPreferencesData(prefetchedPreferencesData.current);
    if (unpackedPrefetchedPrefs !== PrefetchedPreferenceState.INTERNAL) {
      // With a valid set of prefs, set them on the cookies, but don't save to ConsentHub
      updatePreferences(unpackedPrefetchedPrefs, undefined, true);
    }
    loadStoredPrefs();
  }, []);
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isDismissed = _useState6[0],
    setIsDismissed = _useState6[1];
  var closePreferenceModal = useCallback(function () {
    setIsPreferencesModalOpen(false);
  }, [setIsPreferencesModalOpen]);
  var onPreferenceModalSubmit = useCallback(function () {
    closePreferenceModal();
    setIsDismissed(true);
  }, [closePreferenceModal, setIsDismissed]);
  var isEventSent = useRef(false);
  var onClickPreferences = useCallback(function () {
    setIsPreferencesModalOpen(true);
    sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'preferences',
      source: 'cookieConsentBanner'
    });
  }, []);
  var intl = useIntl();
  var _useFlags = useFlags(),
    showFlag = _useFlags.showFlag;
  var showSuccessFlag = useCallback(function () {
    showFlag(_objectSpread(_objectSpread({}, successToastProps()), {}, {
      id: 'cookieConsentBanner',
      description: intl.formatMessage(i18n.successFlagDescription),
      title: intl.formatMessage(i18n.successFlagTitle)
    }));
  }, [intl, showFlag]);
  var showErrorFlag = useCallback(function () {
    showFlag(_objectSpread(_objectSpread({}, errorToastProps()), {}, {
      id: 'cookieConsentBanner',
      description: intl.formatMessage(i18n.errorFlagDescription),
      title: intl.formatMessage(i18n.errorFlagTitle)
    }));
  }, [intl, showFlag]);

  // Sets preferences to allow only Strictly Necessary cookies
  var onClickOnlyNecessary = useCallback( /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(displayedTextMap) {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _context2.prev = 0;
            sendUIEvent({
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'onlyNecessary',
              source: 'cookieConsentBanner'
            });
            _context2.next = 4;
            return updatePreferences(ONLY_NECESSARY_PREFERENCES, displayedTextMap);
          case 4:
            sendTrackEvent({
              source: 'cookieConsentBanner',
              action: 'submitted',
              actionSubject: 'cookieConsentPreferences'
            });
            showSuccessFlag();
            _context2.next = 12;
            break;
          case 8:
            _context2.prev = 8;
            _context2.t0 = _context2["catch"](0);
            Logger.errorWithOperationalEvent({
              action: 'updatePreferencesError',
              attributes: {
                preferences: ONLY_NECESSARY_PREFERENCES,
                product: product
              },
              message: "Failed to update preferences from banner. ".concat(_context2.t0.message || '')
            });
            showErrorFlag();
          case 12:
            _context2.prev = 12;
            setIsDismissed(true);
            return _context2.finish(12);
          case 15:
          case "end":
            return _context2.stop();
        }
      }, _callee2, null, [[0, 8, 12, 15]]);
    }));
    return function (_x) {
      return _ref3.apply(this, arguments);
    };
  }(), [product, showErrorFlag, showSuccessFlag]);

  // Sets preferences to allow all categories of cookies
  var onClickAcceptAll = useCallback( /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(displayedTextMap) {
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) switch (_context3.prev = _context3.next) {
          case 0:
            _context3.prev = 0;
            sendUIEvent({
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'acceptAll',
              source: 'cookieConsentBanner'
            });
            _context3.next = 4;
            return updatePreferences(ACCEPT_ALL_PREFERENCES, displayedTextMap);
          case 4:
            sendTrackEvent({
              source: 'cookieConsentBanner',
              action: 'submitted',
              actionSubject: 'cookieConsentPreferences'
            });
            showSuccessFlag();
            _context3.next = 12;
            break;
          case 8:
            _context3.prev = 8;
            _context3.t0 = _context3["catch"](0);
            Logger.errorWithOperationalEvent({
              action: 'updatePreferencesError',
              attributes: {
                preferences: ACCEPT_ALL_PREFERENCES,
                product: product
              },
              message: "Failed to update preferences from banner. ".concat(_context3.t0.message || '')
            });
            showErrorFlag();
          case 12:
            _context3.prev = 12;
            setIsDismissed(true);
            return _context3.finish(12);
          case 15:
          case "end":
            return _context3.stop();
        }
      }, _callee3, null, [[0, 8, 12, 15]]);
    }));
    return function (_x2) {
      return _ref4.apply(this, arguments);
    };
  }(), [product, showErrorFlag, showSuccessFlag]);
  var onClickTrackingNoticeLink = useCallback(function () {
    sendUIEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'cookiesTrackingNotice',
      source: 'cookieConsentBanner'
    });
  }, []);
  var showBanner = useMemo(function () {
    if (!getBooleanFF('platform.moonjelly.browser-storage-controls-v2')) {
      // In V1, we only checked based on the existence of the preferences aka missing or "INTERNAL" state
      var prefsMissing = preferences === PrefetchedPreferenceState.INTERNAL || !preferences;
      if (isSSR()) {
        // This logic will execute normally in V2, but for now, as SSR prefetching has not been implemented completely
        // just hide the banner if we're missing the data (always will be in SSR-context) and let the UI handle it on rehydration
        return !prefsMissing && !isDismissed;
      }
      return prefsMissing && !isDismissed;
    }

    // Check banner cookie since we have no better method of global state right now
    var shouldShowBannerBit = getCookie(ATLASSIAN_SHOW_BANNER_COOKIE);
    var shouldShowBanner = shouldShowBannerBit ? !!Number(shouldShowBannerBit) : false;
    // When preferences are unknown and not flagged as internal (e.g. undefined), show the banner
    var unknownPreferences = preferences !== PrefetchedPreferenceState.INTERNAL && !preferences;
    return (unknownPreferences || shouldShowBanner) && !isDismissed;
  }, [preferences, isDismissed]);

  // Call onShow/onHide as a side effect of showBanner changing, rather than
  // possibly having to call them in multiple places.
  var prevShowBanner = usePrevious(showBanner);
  useEffect(function () {
    if (showBanner && !prevShowBanner) {
      onShow === null || onShow === void 0 || onShow();
    } else if (!showBanner && prevShowBanner) {
      onHide === null || onHide === void 0 || onHide();
    }
  }, [showBanner, prevShowBanner, onShow, onHide]);
  useEffect(function () {
    if (!isEventSent.current && showBanner) {
      sendScreenEvent({
        name: 'cookieConsentBanner'
      });
      isEventSent.current = true;
    }
  }, [isEventSent, showBanner]);
  if (!getBooleanFF('platform.moonjelly.browser-storage-controls-v2')) {
    // In V1, we have no concept of SSRing, so to be explicit, just check `showBanner`
    if (!showBanner) {
      return null;
    }
  }
  // When coming from SSR, hooks only execute once, and the showBanner memo will not update
  // Instead, if we're on the server and have initial preference data, don't render
  // On later checks (rehydration), this will always fail and then rely on the `showBanner` memo
  var initialSSRRenderWithData = isSSR() && preferences !== PrefetchedPreferenceState.INTERNAL;
  // Don't render consent banner if preferences already exist
  if (initialSSRRenderWithData || !showBanner) {
    return null;
  }
  return /*#__PURE__*/React.createElement(FlagsProvider, null, /*#__PURE__*/React.createElement(BSCIntlProvider, null, /*#__PURE__*/React.createElement(PresentationalConsentBanner, {
    onClickOnlyNecessary: onClickOnlyNecessary,
    onClickAcceptAll: onClickAcceptAll,
    onClickPreferences: onClickPreferences,
    onClickTrackingNoticeLink: onClickTrackingNoticeLink,
    minWidth: minWidth
  }), isPreferencesModalOpen && /*#__PURE__*/React.createElement(PreferenceModal, {
    onClose: closePreferenceModal,
    onSubmit: onPreferenceModalSubmit,
    showSuccessFlag: showSuccessFlag,
    showErrorFlag: showErrorFlag
  })));
};